import axios from "axios"
import Uppy, { type UppyFile, type Meta } from "@uppy/core"
import DropTarget from "@uppy/drop-target"
import XHR from "@uppy/xhr-upload"
import { csrfToken } from "../csrfToken"

const DEFAULT_MAX_FILES = 3
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const DRAGGING_CLASS = "is-dragging"
const UPLOAD_FIELD_NAME = "file"
const COMPLETE_PROGRESS = 100

type FileWithMeta = UppyFile<Meta, Record<string, never>>

/**
 * Represents a file being uploaded or already uploaded.
 * This is a simplified view of Uppy's internal file state.
 */
export interface UploadedFile {
  id: string
  name: string
  signedId?: string
  status: "pending" | "uploading" | "complete" | "error" | "deleting"
  progress: number // 0-100
  error?: string
}

/**
 * Data needed to restore a previously uploaded file.
 */
export interface RestoredFile {
  name: string
  contentType?: string
  signedId: string
  size?: number
}

export interface FileUploaderOptions {
  target: HTMLElement // Drop target element
  endpoint: string // Upload endpoint URL
  maxFiles?: number // Default: 3
  maxFileSize?: number // Default: 5MB (5242880 bytes)
  allowedTypes?: string[] // MIME types
  onStateChange?: (files: UploadedFile[]) => void
  onError?: (message: string) => void
  onDeleteError?: (fileId: string, fileName: string, error: string) => void
}

/**
 * FileUploader wraps the Uppy library to provide a consistent
 * interface for file uploads throughout the application.
 *
 * This encapsulates all Uppy-specific implementation details,
 * allowing the rest of the codebase to remain agnostic of the underlying library.
 */
export class FileUploader {
  private uppy: Uppy
  private options: FileUploaderOptions
  private deletingFiles: Set<string> = new Set() // Track files being deleted
  private stateUpdateHandler: (() => void) | null = null
  private uploadSuccessHandler:
    | ((file: FileWithMeta | undefined, response: { body?: { signed_id?: string } }) => void)
    | null = null
  private fileRemovedHandler: ((file: FileWithMeta) => void) | null = null
  private restrictionFailedHandler:
    | ((file: FileWithMeta | undefined, error: { message: string }) => void)
    | null = null
  private uploadErrorHandler:
    | ((file: FileWithMeta | undefined, error: { message?: string }) => void)
    | null = null

  constructor(options: FileUploaderOptions) {
    this.options = options

    const maxFiles = options.maxFiles ?? DEFAULT_MAX_FILES
    const maxFileSize = options.maxFileSize ?? DEFAULT_MAX_FILE_SIZE

    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        maxNumberOfFiles: maxFiles,
        maxFileSize: maxFileSize,
        allowedFileTypes: options.allowedTypes?.length ? options.allowedTypes : null,
      },
    })
      .use(DropTarget, {
        target: options.target,
        onDragOver: () => options.target.classList.add(DRAGGING_CLASS),
        onDragLeave: () => options.target.classList.remove(DRAGGING_CLASS),
        onDrop: () => options.target.classList.remove(DRAGGING_CLASS),
      })
      .use(XHR, {
        endpoint: options.endpoint,
        fieldName: UPLOAD_FIELD_NAME,
        headers: csrfToken(),
      })

    this.setupEventHandlers()
  }

  /**
   * Add files to the upload queue.
   */
  addFiles(files: FileList): void {
    Array.from(files).forEach((file) => {
      try {
        this.uppy.addFile({ name: file.name, type: file.type, data: file })
      } catch (error) {
        // Restriction errors are also emitted via restriction-failed event
        this.uppy.log(error)
      }
    })
  }

  /**
   * Remove a file from the upload queue.
   * If the file has been uploaded, this will also delete it from the server.
   */
  async removeFile(fileId: string): Promise<void> {
    const file = this.uppy.getFile(fileId)
    if (!file) return

    const signedId = file.meta.signedId as string | undefined

    if (signedId) {
      // File has been uploaded - need to delete from server
      // Show "deleting" state only after 200ms delay (per design guide)
      const spinnerTimeout = setTimeout(() => {
        this.deletingFiles.add(fileId)
        this.notifyStateChange()
      }, 200)

      try {
        await this.deleteFromServer(signedId)
        clearTimeout(spinnerTimeout)
        this.deletingFiles.delete(fileId)
        this.uppy.removeFile(fileId)
      } catch (error) {
        clearTimeout(spinnerTimeout)
        this.deletingFiles.delete(fileId)
        const errorMessage = error instanceof Error ? error.message : "Failed to delete file"
        this.options.onDeleteError?.(fileId, file.name, errorMessage)
        this.notifyStateChange()
      }
    } else {
      // File not yet uploaded - just remove from queue
      this.uppy.removeFile(fileId)
    }
  }

  /**
   * Get the current list of files in a simplified format.
   */
  getFiles(): UploadedFile[] {
    return this.uppy.getFiles().map((file) => this.mapToUploadedFile(file))
  }

  /**
   * Check if more files can be added.
   */
  canAddFiles(): boolean {
    const maxFiles = this.options.maxFiles ?? DEFAULT_MAX_FILES
    return this.uppy.getFiles().length < maxFiles
  }

  /**
   * Restore previously uploaded files (e.g., when editing an existing record).
   */
  restoreFiles(files: RestoredFile[]): void {
    if (!files.length) return

    const previousAutoProceed = this.uppy.opts.autoProceed
    this.uppy.setOptions({ autoProceed: false })

    files.forEach((file) => {
      const fileId = this.uppy.addFile({
        name: file.name,
        type: file.contentType,
        data: new Blob(),
        source: "restored",
        isRemote: true,
        meta: { signedId: file.signedId },
      })

      if (fileId) {
        this.uppy.setFileState(fileId, {
          progress: {
            percentage: COMPLETE_PROGRESS,
            uploadComplete: true,
            uploadStarted: Date.now(),
            bytesTotal: file.size ?? 0,
            bytesUploaded: file.size ?? 0,
          },
        })
      }
    })

    this.uppy.setOptions({ autoProceed: previousAutoProceed })
    this.notifyStateChange()
  }

  /**
   * Clean up resources. Call this when the component is destroyed.
   */
  destroy(): void {
    // Unregister all event handlers before destroy to prevent side effects
    // (e.g., DELETE requests when the controller disconnects during navigation)
    if (this.stateUpdateHandler) this.uppy.off("state-update", this.stateUpdateHandler)
    if (this.uploadSuccessHandler) this.uppy.off("upload-success", this.uploadSuccessHandler)
    if (this.fileRemovedHandler) this.uppy.off("file-removed", this.fileRemovedHandler)
    if (this.restrictionFailedHandler) this.uppy.off("restriction-failed", this.restrictionFailedHandler)
    if (this.uploadErrorHandler) this.uppy.off("upload-error", this.uploadErrorHandler)
    this.uppy.destroy()
  }

  private setupEventHandlers(): void {
    // Re-render UI on any Uppy state change
    this.stateUpdateHandler = () => this.notifyStateChange()
    this.uppy.on("state-update", this.stateUpdateHandler)

    // Store signed_id in file meta on successful upload
    this.uploadSuccessHandler = (file, response) => {
      if (file && response.body?.signed_id) {
        file.meta.signedId = response.body.signed_id
      }
    }
    this.uppy.on("upload-success", this.uploadSuccessHandler)

    // File removal is handled by removeFile() method which manages deletion lifecycle
    // This handler is kept for compatibility but the actual server deletion
    // happens in removeFile() before calling uppy.removeFile()
    this.fileRemovedHandler = () => {
      // No-op: deletion is handled in removeFile() before this event fires
    }
    this.uppy.on("file-removed", this.fileRemovedHandler)

    // Handle restriction errors
    this.restrictionFailedHandler = (_, error) => {
      this.options.onError?.(error.message)
    }
    this.uppy.on("restriction-failed", this.restrictionFailedHandler)

    // Handle upload errors
    this.uploadErrorHandler = (file, error) => {
      console.error("Upload error:", file?.name, error)
      this.options.onError?.(error.message || "Upload failed")
    }
    this.uppy.on("upload-error", this.uploadErrorHandler)
  }

  private notifyStateChange(): void {
    this.options.onStateChange?.(this.getFiles())
  }

  private async deleteFromServer(signedId: string): Promise<void> {
    if (!this.options.endpoint) return

    const url = new URL(this.options.endpoint, window.location.origin)
    url.pathname = `${url.pathname}/${signedId}`
    await axios.delete(url.toString(), { headers: csrfToken() })
  }

  private mapToUploadedFile(file: FileWithMeta): UploadedFile {
    const p = file.progress
    const isDeleting = this.deletingFiles.has(file.id)
    const isUploading = p?.uploadStarted && !p?.uploadComplete
    const isComplete = p?.uploadComplete
    const isError = !!file.error

    let status: UploadedFile["status"]
    if (isDeleting) {
      status = "deleting"
    } else if (isError) {
      status = "error"
    } else if (isComplete) {
      status = "complete"
    } else if (isUploading) {
      status = "uploading"
    } else {
      status = "pending"
    }

    return {
      id: file.id,
      name: file.name,
      signedId: file.meta.signedId as string | undefined,
      status,
      progress: p?.percentage ?? 0,
      error: file.error ?? undefined,
    }
  }
}
